import { Link } from "react-router";
import "./PractitionerRole.scss";

const asArray = (value) => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
};

const dash = (value) => (value == null || value === "" ? "—" : String(value));

const yn = (value) => {
    if (value === true || value === "true" || value === "Y" || value === "y") return "Y";
    if (value === false || value === "false" || value === "N" || value === "n") return "N";
    return value ? "Y" : "N";
};

const formatAddress = (address = {}) =>
    [address.line1, address.line2, [address.city, address.state].filter(Boolean).join(", "), address.zip]
        .filter(Boolean)
        .join(" · ");

const friendlyLocation = (name, address) => {
    const formatted = formatAddress(address);
    if (formatted) return formatted;
    if (!name) return "—";
    const parts = String(name).split("_").filter(Boolean);
    return parts.length > 2 ? parts.slice(2).join(" ") : name;
};

const Flag = ({ on, label }) => (
    <span className={`practitioner-role-detail__flag${on ? " is-on" : ""}`}>{label}</span>
);

const Dl = ({ rows }) => (
    <dl className="practitioner-role-detail__dl">
        {rows.map((row) => (
            <div key={row.label}>
                <dt>{row.label}</dt>
                <dd>{row.value ?? "—"}</dd>
            </div>
        ))}
    </dl>
);

const flattenSoapRole = (item) => {
    const networks = asArray(item?.benefitNetworks?.benefitNetwork);
    return {
        roleName: item?.practitionerRoleName,
        supplierName: item?.supplier?.supplierName,
        supplierHccId: item?.supplier?.supplierHccId,
        locationName: item?.supplierLocation?.supplierLocationName,
        locationHccId: item?.supplierLocation?.supplierLocationHccId,
        specialty: item?.primarySpecialty?.codeName,
        taxonomy: item?.primarySpecialty?.code,
        pcp: yn(item?.pcp),
        specialist: yn(item?.specialist),
        obgyn: yn(item?.obgyn),
        status: item?.instanceActiveCode,
        effective: item?.effectiveStartDate,
        terminated: item?.versionEndDate,
        networks: networks.map((n) => n.benefitNetworkName).filter(Boolean).join(", "),
    };
};

const PractitionerRole = ({ data }) => {
    const details = data?.details || {};
    const role = details.role || data?.role || {};
    const supplier = details.supplier || data?.supplier || {};
    const location = details.location || data?.location || {};
    const specialty = role.specialty || {};
    const address = location.address || {};
    const soapRoles = asArray(details.soapRoles).map(flattenSoapRole);

    const thisRoleName = role.name || data.roleName;
    const currentSoap = soapRoles.find((r) => r.roleName === thisRoleName)
        || soapRoles.find((r) => r.supplierHccId === supplier.hccId && r.locationHccId === location.hccId);

    const displayName = data.practitionerName
        || [data.firstName, data.lastName].filter(Boolean).join(" ")
        || "Role";

    return (
        <div className="practitioner-role-detail">
            <p className="practitioner-role-detail__kicker">Practitioner role</p>
            <p className="title">{displayName}</p>
            <p className="practitioner-role-detail__lede">
                A claim uses this role at this clinic, not the person alone.{" "}
                <Link to="/providers/guide">How this wires up in HRP</Link>
            </p>

            <div className="practitioner-role-detail__meta">
                <span>HCC <code>{dash(data.practitionerHccId)}</code></span>
                <span>NPI <code>{dash(data.npi)}</code></span>
                {data.phone ? <span>Phone {data.phone}</span> : null}
            </div>

            <div className="practitioner-role-detail__flags">
                <Flag on={yn(role.isPcp) === "Y"} label="PCP" />
                <Flag on={yn(role.isSpecialist) === "Y"} label="Specialist" />
                <Flag on={yn(role.isObgyn) === "Y"} label="OB/GYN" />
                <Flag on={String(role.status || data.roleStatus || "").toLowerCase() === "a"} label={role.status || data.roleStatus || "status"} />
            </div>

            <div className="practitioner-role-detail__grid">
                <section>
                    <h2>This role</h2>
                    <Dl
                        rows={[
                            { label: "Role name", value: <code>{dash(thisRoleName)}</code> },
                            { label: "Specialty", value: dash(specialty.name) },
                            { label: "Classification", value: dash(specialty.classification) },
                            { label: "Taxonomy", value: specialty.taxonomyCode ? <code>{specialty.taxonomyCode}</code> : "—" },
                            { label: "Effective", value: dash(role.effectiveDate) },
                            { label: "Terminated", value: dash(role.terminationDate) },
                        ]}
                    />
                </section>
                <section>
                    <h2>At this supplier</h2>
                    <Dl
                        rows={[
                            { label: "Supplier", value: dash(supplier.name) },
                            { label: "Supplier HCC", value: supplier.hccId ? <code>{supplier.hccId}</code> : "—" },
                            { label: "Supplier NPI", value: supplier.npi ? <code>{supplier.npi}</code> : "—" },
                            { label: "Location HCC", value: location.hccId ? <code>{location.hccId}</code> : "—" },
                            { label: "Address", value: friendlyLocation(location.name, address) },
                        ]}
                    />
                    <p className="practitioner-role-detail__jump">
                        <Link to="/providers/suppliers">Open suppliers</Link>
                        {" · "}
                        <Link to="/providers/practitioners">Open practitioners</Link>
                    </p>
                </section>
            </div>

            {currentSoap?.networks ? (
                <p className="practitioner-role-detail__networks">
                    Benefit networks on this SOAP role: <strong>{currentSoap.networks}</strong>
                </p>
            ) : null}

            <h2>
                All roles for this practitioner
                {soapRoles.length ? <span> {soapRoles.length}</span> : null}
            </h2>
            {soapRoles.length ? (
                <div className="table-wrapper practitioner-role-detail__table">
                    <table>
                        <thead>
                            <tr>
                                <th>role</th>
                                <th>supplier</th>
                                <th>location</th>
                                <th>specialty</th>
                                <th>pcp</th>
                                <th>specialist</th>
                                <th>status</th>
                                <th>effective</th>
                                <th>benefit networks</th>
                            </tr>
                        </thead>
                        <tbody>
                            {soapRoles.map((row, i) => {
                                const isCurrent = [row.roleName, row.supplierHccId, row.locationHccId].join("-")
                                    === [thisRoleName, supplier.hccId, location.hccId].join("-")
                                    || row.roleName === thisRoleName;
                                return (
                                    <tr key={`${row.roleName || i}-${row.locationHccId || i}`} className={isCurrent ? "is-current" : undefined}>
                                        <td><code>{dash(row.roleName)}</code></td>
                                        <td>{dash(row.supplierName)}{row.supplierHccId ? <><br /><code>{row.supplierHccId}</code></> : null}</td>
                                        <td>{dash(row.locationName)}{row.locationHccId ? <><br /><code>{row.locationHccId}</code></> : null}</td>
                                        <td>{dash(row.specialty)}{row.taxonomy ? <><br /><code>{row.taxonomy}</code></> : null}</td>
                                        <td>{dash(row.pcp)}</td>
                                        <td>{dash(row.specialist)}</td>
                                        <td>{dash(row.status)}</td>
                                        <td>{dash(row.effective)}</td>
                                        <td>{dash(row.networks)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="sub-title">No other roles returned from getPractitionerRole.</p>
            )}
        </div>
    );
};

export default PractitionerRole;
